/**
 * Single source of truth for Bearer-authed fetches against the
 * /api/storage/* + /api/probe-key endpoints.
 *
 * Every storage / proxy server route runs requireFirebaseAuth, so a missing
 * Authorization header silently 401s. Inline `fetch` calls were drifting
 * across the codebase — some attached the token, some didn't — and the
 * silent failures hid real bugs (the SED-save 401, the document-upload
 * 401). This helper fixes that with one well-typed wrapper.
 *
 * Usage mirrors the standard fetch:
 *
 *   const res = await authedFetch('/api/storage/save', {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify({ path, data }),
 *   });
 *   if (!res.ok) throw new Error(`save failed (${res.status})`);
 *
 * Implementation notes:
 *   - Firebase Authorization is injected only for same-origin requests. A
 *     caller-supplied Authorization header is still preserved (lets bespoke
 *     flows plug in their own credential deliberately), but this helper will
 *     never mint a Firebase token for an attacker-controlled origin.
 *   - When no user is signed in, the request goes out without a token —
 *     the server's auth gate returns 401 and the caller surfaces a clean
 *     "Not signed in" error rather than this helper guessing.
 *   - Dynamic-imports `firebase` so this module stays browser-loadable
 *     under SSR / replay-script paths that don't initialise Firebase.
 */

import { classifyStorageFailure } from './classifyStorageFailure';
import { awaitAbortableRead, throwIfReadAborted } from './abortableRead';

type AuthedFetchInput = string | URL | Request;

const STORAGE_AUTH_TOAST_COOLDOWN_MS = 30_000;
let lastStorageAuthToastAt = 0;

function requestUrl(input: AuthedFetchInput): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/**
 * Resolve with the browser's real origin when available. In non-browser test/SSR runtimes only a
 * relative reference can be proven local; absolute URLs fail closed because there is no ambient
 * origin to compare against.
 */
function isSameOriginRequest(input: AuthedFetchInput): boolean {
  const raw = requestUrl(input);
  try {
    if (typeof window !== 'undefined' && window.location?.href) {
      return new URL(raw, window.location.href).origin === window.location.origin;
    }
    const sentinel = new URL('https://authed-fetch.invalid/');
    return new URL(raw, sentinel).origin === sentinel.origin
      && !/^[a-z][a-z\d+.-]*:/i.test(raw)
      && !raw.startsWith('//');
  } catch {
    return false;
  }
}

async function resolveBearerToken(
  user: { getIdToken: (forceRefresh?: boolean) => Promise<string> },
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const token = await awaitAbortableRead(user.getIdToken(), signal);
    return token || null;
  } catch {
    throwIfReadAborted(signal);
    try {
      const token = await awaitAbortableRead(user.getIdToken(true), signal);
      return token || null;
    } catch {
      throwIfReadAborted(signal);
      return null;
    }
  }
}

/** undici / modern fetch reject non-realm-local AbortSignal values at init time. */
function resolveFetchSignal(signal: RequestInit['signal']): AbortSignal | undefined {
  if (signal == null) return undefined;
  if (!(signal instanceof AbortSignal)) return undefined;
  try {
    new Request('about:blank', { signal });
    return signal;
  } catch {
    return undefined;
  }
}

function isAbortSignalTypeError(error: unknown): boolean {
  // #118 — some runtimes/polyfills throw a NON-TypeError (and a differently
  // worded "...instanceof AbortSignal", one word) at fetch init for a
  // realm-mismatched signal. Match ANY Error mentioning AbortSignal so the
  // no-signal retry below fires for every variant (uploads share authedFetch,
  // so this unblocks hero-image / bug-attachment / logo uploads alike).
  return error instanceof Error && /AbortSignal/i.test(error.message);
}

/**
 * AbortController-based fetch timeout — same realm as the page's native
 * `fetch`. Prefer this over `AbortSignal.timeout()`, which can pass
 * `instanceof AbortSignal` in JS but still fail at fetch init in prod.
 */
export function createFetchAbortTimeout(ms: number): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, cancel: () => clearTimeout(timer) };
}

async function maybeNotifyStorageFailure(input: AuthedFetchInput, res: Response): Promise<void> {
  const url = requestUrl(input);
  if (!url.includes('/api/storage/') || res.ok) return;
  if (Date.now() - lastStorageAuthToastAt < STORAGE_AUTH_TOAST_COOLDOWN_MS) return;

  let body: unknown = '';
  try {
    const snapshot = res.clone();
    const raw = await snapshot.text();
    try {
      body = raw ? JSON.parse(raw) : '';
    } catch {
      body = raw;
    }
  } catch {
    body = '';
  }

  const classified = classifyStorageFailure({ status: res.status, body });
  if (!classified.shouldNotify) return;

  lastStorageAuthToastAt = Date.now();
  try {
    const { toast } = await import('sonner');
    toast.error(classified.userMessage, { id: 'storage-auth-error', duration: 12_000 });
  } catch {
    console.warn('[authedFetch]', classified.userMessage);
  }
}

export async function authedFetch(
  input: AuthedFetchInput,
  init: RequestInit = {},
): Promise<Response> {
  const { signal, ...restInit } = init;
  const readSignal = signal instanceof AbortSignal ? signal : undefined;
  const resolvedSignal = resolveFetchSignal(signal);
  throwIfReadAborted(readSignal);
  const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
  if (isSameOriginRequest(input) && !headers.has('Authorization')) {
    try {
      const { auth } = await awaitAbortableRead(import('../firebase'), readSignal);
      const user = auth.currentUser;
      if (user) {
        const token = await resolveBearerToken(user, readSignal);
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        } else {
          console.warn('[authedFetch] signed-in user but no Firebase ID token — storage call may 401');
        }
      }
    } catch {
      throwIfReadAborted(readSignal);
      // Firebase unavailable in this runtime — fall through. The server's
      // 401 is a more useful error than swallowing the call here.
    }
  }
  throwIfReadAborted(readSignal);
  const fetchInit: RequestInit = { ...restInit, headers };
  if (resolvedSignal) fetchInit.signal = resolvedSignal;
  try {
    const res = await awaitAbortableRead(fetch(input as RequestInfo | URL, fetchInit), readSignal);
    void maybeNotifyStorageFailure(input, res);
    return res;
  } catch (error) {
    throwIfReadAborted(readSignal);
    if (resolvedSignal && isAbortSignalTypeError(error)) {
      const res = await awaitAbortableRead(fetch(input as RequestInfo | URL, { ...restInit, headers }), readSignal);
      void maybeNotifyStorageFailure(input, res);
      return res;
    }
    throw error;
  }
}

/** Test-only reset for toast debounce. */
export function __resetStorageAuthToastForTest(): void {
  lastStorageAuthToastAt = 0;
}
